import { TimeFormat } from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

const MINUTES_PER_HOUR = 60;

const WALL_CLOCK_FORMAT = 'HH:mm';

export const SITE_TIME_FORMAT_BY_TIME_FORMAT: Record<TimeFormat, string> = {
  [TimeFormat['12_HOUR']]: 'hh:mm A',
  [TimeFormat['24_HOUR']]: 'HH:mm',
};

// Wall-clock times carry no date or zone, so they are pinned to a fixed winter date in
// plain local time: `dayjs.tz` would shift them, and a DST-transition day can drop an hour.
const WALL_CLOCK_ANCHOR_DATE = '2000-01-01';

export const fromWallClock = (value: string | null) => {
  const [hour, minute] = value?.split(':').map(Number) ?? [];
  const isParseableWallClock = Number.isInteger(hour) && Number.isInteger(minute);
  if (!isParseableWallClock) {
    return null;
  }

  return dayjs(WALL_CLOCK_ANCHOR_DATE).hour(hour).minute(minute);
};

export const toWallClock = (value: Dayjs | null) =>
  value?.isValid() ? value.format(WALL_CLOCK_FORMAT) : null;

const formatTimeZoneName = (
  timeZone: string,
  at: Dayjs,
  timeZoneName: Intl.DateTimeFormatOptions['timeZoneName'],
) =>
  new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName })
    .formatToParts(at.toDate())
    .find((part) => part.type === 'timeZoneName')?.value ?? timeZone;

/** "CDT" in summer, "CST" in winter. */
export const getTimeZoneAbbreviation = (timeZone: string, at: Dayjs) =>
  formatTimeZoneName(timeZone, at, 'short');

/** "Central Daylight Time" in summer, "Central Standard Time" in winter. */
export const getTimeZoneLongName = (timeZone: string, at: Dayjs) =>
  formatTimeZoneName(timeZone, at, 'long');

const getUtcOffsetMinutes = (timeZone: string, at: Dayjs) => at.tz(timeZone).utcOffset();

/** Positive when the user's clock is ahead of the site's. */
export const getHoursAheadOfSite = (
  siteTimeZone: string,
  userTimeZone: string,
  at: Dayjs,
) =>
  (getUtcOffsetMinutes(userTimeZone, at) - getUtcOffsetMinutes(siteTimeZone, at)) /
  MINUTES_PER_HOUR;

/** A site's "HH:mm" wall-clock time on `onDate`'s site-local day, seen from `toTimeZone`. */
export const convertWallClockToTimeZone = (
  wallClock: string,
  siteTimeZone: string,
  toTimeZone: string,
  onDate: Dayjs,
) => {
  const siteLocalDate = onDate.tz(siteTimeZone).format('YYYY-MM-DD');

  return dayjs
    .tz(`${siteLocalDate} ${wallClock}`, 'YYYY-MM-DD HH:mm', siteTimeZone)
    .tz(toTimeZone);
};
